import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { coy, vs } from "react-syntax-highlighter/dist/esm/styles/prism";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useState } from "react";

const parseString = (str) => {
    try {
        const jsonLikeStr = str
            .replace(/'/g, '"')
            .replace(/None/g, "null")
            .replace(/True/g, "true")
            .replace(/False/g, "false");

        return JSON.parse(jsonLikeStr);
    } catch (e) {
        console.error("Failed to parse Python-like string:", e);
        return null;
    }
};

const extractTitleAndObject = (combinedString) => {
    let title = combinedString;
    let objectString = null;
    let parsedObject = null;

    const objectStartIndex = combinedString.indexOf("{");

    if (objectStartIndex !== -1) {
        title = combinedString.substring(0, objectStartIndex).trim();
        objectString = combinedString.substring(objectStartIndex);
        parsedObject = parseString(objectString);
    }

    return { title, objectString, parsedObject };
};

const LogDetail = ({ label, value, isError = false }) => {
    const [copySuccess, setCopySuccess] = useState(false);

    if (!value && !isError) return null;

    const { title, parsedObject } =
        typeof value === "string"
            ? extractTitleAndObject(value)
            : { title: null, objectString: null, parsedObject: value };

    const handleCopy = () => {
        let textToCopy = value || "";

        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            })
            .catch((err) => console.error("Failed to copy: ", err));
    };

    const renderObjectValue = () => {
        if (!parsedObject) return null;

        try {
            const jsonString = JSON.stringify(parsedObject, null, 2);
            return (
                <SyntaxHighlighter
                    language="json"
                    style={isError ? vs : coy}
                    customStyle={{
                        margin: 0,
                        padding: "12px",
                        borderRadius: "4px",
                        maxHeight: "250px",
                        fontSize: "13px",
                    }}
                >
                    {jsonString}
                </SyntaxHighlighter>
            );
        } catch (e) {
            return String(parsedObject) || "—";
        }
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                }}
            >
                <DetailLabel
                    variant="subtitle2"
                    color={isError ? "error" : "textSecondary"}
                >
                    {label}
                </DetailLabel>

                <Tooltip title={copySuccess ? "Copied!" : "Copy to clipboard"}>
                    <IconButton
                        size="small"
                        onClick={handleCopy}
                        sx={{
                            fontSize: "12px",
                            color: (theme) =>
                                copySuccess
                                    ? theme.palette.success.main
                                    : "inherit",
                            "&:hover": {
                                backgroundColor: (theme) =>
                                    alpha(theme.palette.primary.main, 0.1),
                            },
                        }}
                    >
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box
                sx={{
                    borderLeft: (theme) =>
                        `3px solid ${
                            isError
                                ? theme.palette.error.main
                                : theme.palette.primary.main
                        }`,
                    borderRadius: 1,
                    overflow: "hidden",
                }}
            >
                {title && (
                    <DetailContent isError={isError}>{title}</DetailContent>
                )}
                {parsedObject && renderObjectValue()}
                {!parsedObject && !title && (
                    <DetailContent isError={isError}>
                        {value || "—"}
                    </DetailContent>
                )}
            </Box>
        </Box>
    );
};

export default LogDetail;

const DetailContent = styled(Box)(({ theme, isError }) => ({
    backgroundColor: isError
        ? alpha(theme.palette.error.light, 0.08)
        : alpha("#f5f5f5", 0.8),
    color: isError ? theme.palette.error.main : "inherit",
    fontWeight: isError ? 600 : 400,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    maxHeight: 250,
    overflow: "auto",
    fontFamily: "monospace",
    fontSize: "13px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    color: theme.palette.text.secondary,
}));
